import SoftBadge from "@/components/atoms/SoftBadge";
import AllCoursesHeader from "@/components/molecules/table-headers/AllCoursesHeader";
import Table from "@/components/molecules/table/Table";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useTableState } from "@/hooks/useTableState";
import Course from "@/interfaces/Course";
import { useGetCoursesQuery } from "@/services/courses";
import { faEdit, faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnDef, TableState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { FC, useRef } from "react";
import { Link } from "react-router-dom";

const CoursesView: FC = () => {
  const [state, setState] = useTableState<Partial<TableState>>("courses", {
    pagination: {
      pageIndex: 0,
      pageSize: 15,
    },
    globalFilter: "",
  });

  const debouncedQuery = useDebouncedValue(state.globalFilter, 500);

  const { data } = useGetCoursesQuery({
    ...state.pagination,
    query: debouncedQuery,
    ...(state.sorting?.[0] && {
      sortBy: state.sorting[0].id,
      sortDirection: state.sorting[0].desc ? "desc" : "asc",
    }),
  });

  const columns = useRef<ColumnDef<Course>[]>([
    {
      header: "Actions",
      enableSorting: false,
      footer: (props) => props.column.id,
      accessorFn: ({ id }) => (
        <>
          <Link to={`/courses/${id}`} className="btn btn-primary btn-sm">
            <FontAwesomeIcon icon={faEye} />
          </Link>
          <Link to={`/courses/${id}/edit`} className="btn btn-info btn-sm ms-2">
            <FontAwesomeIcon icon={faEdit} />
          </Link>
        </>
      ),
      id: "actions",
      cell: (info) => info.getValue(),
    },
    {
      header: "Nom",
      footer: (props) => props.column.id,
      accessorFn: ({ name }) => name,
      id: "name",
      cell: (info) => info.getValue(),
    },
    {
      header: "Archivé",
      footer: (props) => props.column.id,
      accessorFn: ({ archived }) => (
        <SoftBadge bg={archived ? "danger" : "success"}>
          {archived ? "Oui" : "Non"}
        </SoftBadge>
      ),
      id: "archived",
      cell: (info) => info.getValue(),
    },
    {
      header: "Crée le",
      footer: (props) => props.column.id,
      accessorFn: (row) =>
        DateTime.fromISO(row.createdAt).toFormat("dd-MM-yyyy"),
      id: "createdAt",
      cell: (info) => info.getValue(),
    },
  ]).current;

  return (
    <div className="formalys-page formalys-list-page">
      <Table
        resource="courses"
        tableName="courses"
        HeaderComponent={AllCoursesHeader}
        columns={columns}
        state={state}
        onStateChange={setState}
        data={data?.data}
        meta={data?.meta}
      />
    </div>
  );
};

export default CoursesView;
